import { useEffect, useRef } from "react";

import {
  focusElement,
  selectorFocusableElements
} from "../utils/keyboard-navigation/keyboard-navigation";

/**
 * @param {*} isFocus - defines whether focusing is enabled or not.
 * @param elementRef - focusable element or element with focusable children.
 * @param {?{
 *   isScrollFocusedElementIntoView?: boolean,
 *   isFocusOnFirstIteration?: boolean,
 *   onAfterFocus?: () => void,
 * }} options
 */
const useFocus = (isFocus, elementRef, options) => {
  let isScrollFocusedElementIntoView = true;
  let isFocusOnFirstIteration = true;
  let onAfterFocus = undefined;

  if (options) {
    isScrollFocusedElementIntoView = options.isScrollFocusedElementIntoView;
    isFocusOnFirstIteration = options.isFocusOnFirstIteration;
    onAfterFocus = options.onAfterFocus;
  }

  const refIsFirstIteration = useRef(true);

  // Focuses itself or a suitable element inside after opened.
  useEffect(() => {
    let elFocusedPrev;

    if (refIsFirstIteration.current) {
      refIsFirstIteration.current = false;

      if (!isFocusOnFirstIteration) {
        return undefined;
      }
    }

    if (isFocus && elementRef.current) {
      elFocusedPrev = document.activeElement;

      const focusableElements = [
        ...elementRef.current.querySelectorAll(selectorFocusableElements)
      ];

      const elFocusFirst = focusableElements.find((el) => el.hasAttribute("data-focus-first"));

      if (elFocusFirst) {
        focusElement([elFocusFirst], !isScrollFocusedElementIntoView);
      } else if (focusableElements.length) {
        focusElement(focusableElements, !isScrollFocusedElementIntoView);
      } else {
        focusElement([elementRef.current], !isScrollFocusedElementIntoView);
      }

      if (onAfterFocus) {
        onAfterFocus();
      }
    }

    return () => {
      if (isFocus && elFocusedPrev) {
        elFocusedPrev.focus();
      }
    };
  }, [isFocus]);
};

export default useFocus;
